import { Accordion } from '@liveauctioneers/hammer-ui-core/accordion';
import { Checkbox } from '@liveauctioneers/hammer-ui-core/checkbox';
import { Chip } from '@liveauctioneers/hammer-ui-core/chips';
import { FormattedMessage } from '@liveauctioneers/hammer-ui-core/intl';
import { useCallback } from 'react';
import { useLocation } from 'react-router-dom';

import { useNavigateSearchPageWithParams } from '@/pages/SearchPageSimplified/hooks/useNavigateSearchPageWithParams';
import { CurrentOpenMenu } from '@/pages/SearchPageSimplified/types';
import { getRoundedNumberText } from '@/pages/SearchPageSimplified/utils/getRoundedNumberText';
import { useAppSelector } from '@/redux/hooks';
import { getMergedOptionFacet } from '@/redux/modules/searchFacetAggregates.selectors';
import { FacetOption } from '@/types/FacetOption';
import FacetTypes from '@/types/search/enums/facetTypes';
import { FilterAction } from '@/types/SearchFacet';
import { QuickFilterName, useTrackQuickFilterPillClicked } from '@/utils/amplitude/analytics/searchPage';
import { modifyParamsRemovePage, modifyParamsWhenChecked, parseQueryParams } from '@/utils/queryParams';

type Props = {
    isAllFilters?: boolean;
    isArchivedSearch: boolean;
};

export const RatingFilter = ({ isAllFilters, isArchivedSearch }: Props) => {
    const { search } = useLocation();
    const queryParams = parseQueryParams(search);
    const navigateWithParams = useNavigateSearchPageWithParams();
    const trackQuickFilterPillClicked = useTrackQuickFilterPillClicked();
    const topRatedFacetFacet = useAppSelector((state) =>
        getMergedOptionFacet(state, isArchivedSearch, FacetTypes.TOP_RATED_ID)
    );
    const { options = [] } = topRatedFacetFacet;

    const topRatedFacetOption: FacetOption | null = options.length > 0 ? options[0] : null;

    const onTopRatedOptionClick = useCallback(() => {
        if (!isAllFilters) {
            trackQuickFilterPillClicked(QuickFilterName.TopRatedHouse);
        }

        const newCheckStatus = !topRatedFacetOption.selected;

        let newParams = modifyParamsWhenChecked(
            queryParams,
            FacetTypes.TOP_RATED_ID,
            topRatedFacetOption.id,
            newCheckStatus
        );

        newParams = modifyParamsRemovePage(newParams);

        navigateWithParams({
            currentOpenMenu: isAllFilters ? CurrentOpenMenu.AllFilters : undefined,
            filterAction: newCheckStatus ? FilterAction.RatingChanged : FilterAction.RatingRemoved,
            params: newParams,
            preventScrollReset: true,
        });
    }, [isAllFilters, navigateWithParams, queryParams, topRatedFacetOption, trackQuickFilterPillClicked]);

    if (!topRatedFacetOption || (topRatedFacetOption.count === 0 && !topRatedFacetOption.selected)) {
        return null;
    }

    if (isAllFilters) {
        const mainContent = (
            <Checkbox.Option
                checked={topRatedFacetOption.selected}
                disabled={topRatedFacetOption.count === 0}
                key={topRatedFacetOption.id}
                onChange={onTopRatedOptionClick}
            >
                {topRatedFacetOption.name} ({getRoundedNumberText({ totalFound: topRatedFacetOption.count })})
            </Checkbox.Option>
        );

        return (
            <Accordion
                className="w-full"
                density="compact"
                emphasis="subtle"
                headline="House Rating"
                open
            >
                {mainContent}
            </Accordion>
        );
    }

    return (
        <Chip
            id="top-rated-house"
            onClick={onTopRatedOptionClick}
            selected={topRatedFacetOption.selected}
            style="selectable"
        >
            <FormattedMessage id="searchPage.topRated" />
        </Chip>
    );
};
